import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Shield, ArrowLeft, CheckCircle, AlertTriangle, Clock, Copy, RefreshCw, ExternalLink, BookOpen, Code, Zap, Target, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export default function AIRemediation() {
  const { isDarkMode } = useTheme();
  const { vulnerabilityId } = useParams();
  const navigate = useNavigate();
  const [vulnerability, setVulnerability] = useState(null);
  const [aiRemediation, setAiRemediation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [additionalResources, setAdditionalResources] = useState([]);

  useEffect(() => {
    fetchVulnerability();
  }, [vulnerabilityId]);

  const fetchVulnerability = async () => {
    try {
      const response = await api.get(`/vulnerabilities/${vulnerabilityId}`);
      setVulnerability(response.data);
      
      // Check if AI remediation already exists
      if (response.data.ai_remediation) {
        setAiRemediation(response.data.ai_remediation);
        // Fetch additional resources based on CWE
        await fetchAdditionalResources(response.data.cwe || response.data.cwe_id);
      }
    } catch (error) {
      toast.error('Failed to fetch vulnerability');
    } finally {
      setLoading(false);
    }
  };

  const generateAIRemediation = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/vulnerabilities/${vulnerabilityId}/ai-remediation`);
      if (response.data.success) {
        setAiRemediation(response.data);
        toast.success('AI remediation generated successfully');
        // Fetch additional resources
        await fetchAdditionalResources(vulnerability.cwe || vulnerability.cwe_id);
      } else {
        toast.error(response.data.error || 'Failed to generate remediation');
      }
    } catch (error) {
      toast.error('Failed to generate AI remediation');
    } finally {
      setGenerating(false);
    }
  };

  const fetchAdditionalResources = async (cweId) => {
    if (!cweId) return;
    
    // Generate relevant resources based on CWE ID
    const cweNumber = cweId.replace('CWE-', '');
    const resources = [
      {
        title: `CWE-${cweNumber} Official Documentation`,
        url: `https://cwe.mitre.org/data/definitions/${cweNumber}.html`,
        type: 'documentation',
        source: 'MITRE CWE'
      },
      {
        title: `OWASP ${vulnerability?.category || 'Security'} Prevention Cheat Sheet`,
        url: `https://cheatsheetseries.owasp.org/`,
        type: 'cheatsheet',
        source: 'OWASP'
      },
      {
        title: `NVD Database - ${vulnerability?.cve_id || cweId}`,
        url: vulnerability?.cve_id 
          ? `https://nvd.nist.gov/vuln/detail/${vulnerability.cve_id}`
          : `https://nvd.nist.gov/vuln/search/results?form_type=Basic&results_type=overview&query=${cweId}`,
        type: 'database',
        source: 'NVD'
      }
    ];

    // Add technology-specific resources
    const techStack = vulnerability?.technology_stack || vulnerability?.affected_component || '';
    if (techStack.toLowerCase().includes('node') || techStack.toLowerCase().includes('javascript')) {
      resources.push({
        title: 'Node.js Security Best Practices',
        url: 'https://nodejs.org/en/docs/guides/security/',
        type: 'guide',
        source: 'Node.js'
      });
    }
    if (techStack.toLowerCase().includes('python')) {
      resources.push({
        title: 'Python Security Best Practices',
        url: 'https://python-security.readthedocs.io/',
        type: 'guide',
        source: 'Python Security'
      });
    }
    if (techStack.toLowerCase().includes('sql') || cweId.includes('89')) {
      resources.push({
        title: 'SQL Injection Prevention Cheat Sheet',
        url: 'https://cheatsheetseries.owasp.org/cheatsheets/SQL_Injection_Prevention_Cheat_Sheet.html',
        type: 'cheatsheet',
        source: 'OWASP'
      });
    }

    setAdditionalResources(resources);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleStepComplete = (stepIndex) => {
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return colors[severity] || colors.medium;
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'documentation': return <FileText className="w-4 h-4" />;
      case 'cheatsheet': return <BookOpen className="w-4 h-4" />;
      case 'database': return <Target className="w-4 h-4" />;
      case 'guide': return <Code className="w-4 h-4" />;
      default: return <ExternalLink className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-lg">Loading remediation guidance...</div>
      </div>
    );
  }

  if (!vulnerability) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertTriangle className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400 font-inter text-sm">Vulnerability not found</p>
        <Button onClick={() => navigate('/vulnerability-hub')} className="mt-4">
          Back to Vulnerability Hub
        </Button>
      </div>
    );
  }

  const technicalSteps = aiRemediation?.technical_steps || [];
  const immediateActions = aiRemediation?.immediate_actions || [];
  const verificationSteps = aiRemediation?.verification_steps || [];
  const preventionMeasures = aiRemediation?.prevention_measures || [];
  const estimatedEffort = aiRemediation?.estimated_effort || {};

  const totalSteps = technicalSteps.length;
  const completionPercentage = totalSteps > 0 ? (completedSteps.size / totalSteps) * 100 : 0;

  return (
    <div data-testid="ai-remediation-container" className="space-y-5">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vulnerability-hub')}
          className={isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-12 h-12 bg-emerald-600/20 border rounded-xl flex items-center justify-center ${isDarkMode ? 'border-emerald-500/30' : 'border-emerald-300'}`}>
            <Shield className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className={`font-rajdhani font-bold text-3xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
              AI Remediation Guidance
            </h1>
            <p className={`font-inter text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              AI-powered step-by-step instructions to resolve this vulnerability
            </p>
          </div>
        </div>
      </div>

      {/* Vulnerability Overview */}
      <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase border ${getSeverityColor(vulnerability.severity)}`}>
                {vulnerability.severity}
              </span>
              {vulnerability.cve_id && (
                <span className={`text-xs font-mono ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{vulnerability.cve_id}</span>
              )}
              {(vulnerability.cwe || vulnerability.cwe_id) && (
                <span className={`text-xs font-mono ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{vulnerability.cwe || vulnerability.cwe_id}</span>
              )}
            </div>
            
            <h2 className={`font-rajdhani font-semibold text-2xl mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {vulnerability.title}
            </h2>
            
            <p className={`font-inter text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
              {vulnerability.description}
            </p>
          </div>
        </div>
      </div>

      {/* Generate AI Remediation Button */}
      {!aiRemediation && (
        <div className={`rounded-2xl p-6 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <Zap className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <h3 className={`font-rajdhani font-semibold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Generate AI Remediation
          </h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Get AI-powered, vulnerability-specific remediation guidance tailored to {vulnerability.cwe || vulnerability.cwe_id || 'this vulnerability'}
          </p>
          <Button
            onClick={generateAIRemediation}
            disabled={generating}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="generate-ai-remediation-btn"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Generate AI Analysis
              </>
            )}
          </Button>
        </div>
      )}

      {/* AI Remediation Content */}
      {aiRemediation && (
        <>
          {/* Progress and Estimated Effort */}
          <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`font-rajdhani font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Progress</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Completion</span>
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {completedSteps.size}/{totalSteps} steps ({Math.round(completionPercentage)}%)
                  </span>
                </div>
                <Progress value={completionPercentage} className="h-2" />
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-400" />
                  <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Estimated:</span>
                  <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {estimatedEffort.hours || '2-4'} hours
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-400" />
                  <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Difficulty:</span>
                  <span className={`font-semibold capitalize ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {estimatedEffort.difficulty || 'Medium'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Immediate Actions */}
          {immediateActions.length > 0 && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                <AlertTriangle className="w-5 h-5" />
                Immediate Actions Required
              </h3>
              <ul className="space-y-2">
                {immediateActions.map((action, idx) => (
                  <li key={idx} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    <span className="text-red-500 mt-0.5">•</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical Remediation Steps */}
          {technicalSteps.length > 0 && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Technical Remediation Steps
              </h3>
              <div className="space-y-4">
                {technicalSteps.map((step, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-xl border transition-all ${
                      completedSteps.has(idx)
                        ? (isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200')
                        : (isDarkMode ? 'bg-zinc-800/50 border-white/10' : 'bg-slate-50 border-slate-200')
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => toggleStepComplete(idx)}
                        className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          completedSteps.has(idx)
                            ? 'bg-emerald-500 text-white'
                            : (isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-200 text-slate-500')
                        }`}
                        data-testid={`step-${idx}-checkbox`}
                      >
                        {completedSteps.has(idx) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <span className="text-xs font-semibold">{step.step || idx + 1}</span>
                        )}
                      </button>
                      <div className="flex-1">
                        <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {step.title}
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {step.description}
                        </p>
                        {step.code_example && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Code Example</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCopyToClipboard(step.code_example)}
                                className="h-6 px-2"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy
                              </Button>
                            </div>
                            <pre className={`p-3 rounded-lg text-xs overflow-x-auto ${isDarkMode ? 'bg-zinc-900 text-zinc-300' : 'bg-slate-800 text-slate-200'}`}>
                              {step.code_example}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verification Steps */}
          {verificationSteps.length > 0 && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Verification Steps
              </h3>
              <ul className="space-y-2">
                {verificationSteps.map((step, idx) => (
                  <li key={idx} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prevention Measures */}
          {preventionMeasures.length > 0 && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Shield className="w-5 h-5 text-indigo-500" />
                Prevention Measures
              </h3>
              <ul className="space-y-2">
                {preventionMeasures.map((measure, idx) => (
                  <li key={idx} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                    <span className="text-indigo-500 mt-0.5">→</span>
                    {measure}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Resources */}
          {additionalResources.length > 0 && (
            <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`font-rajdhani font-semibold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <BookOpen className="w-5 h-5 text-indigo-500" />
                Additional Resources
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {additionalResources.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-3 rounded-xl border flex items-center gap-3 transition-all hover:scale-[1.02] ${
                      isDarkMode 
                        ? 'bg-zinc-800/50 border-white/10 hover:border-indigo-500/30' 
                        : 'bg-slate-50 border-slate-200 hover:border-indigo-300'
                    }`}
                    data-testid={`resource-link-${idx}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
                      {getResourceIcon(resource.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {resource.title}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {resource.source}
                      </p>
                    </div>
                    <ExternalLink className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Regenerate Button */}
          <div className="flex justify-center">
            <Button
              onClick={generateAIRemediation}
              disabled={generating}
              variant="outline"
              className={isDarkMode ? 'border-white/10' : ''}
              data-testid="regenerate-ai-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Analysis
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
